import React from "react";
import { Loading } from "@/components/LoadingState";
import { Edit, Trash2, Award, Receipt, FileDown, MoreVertical } from "lucide-react";
import { formatTL, fmtDate } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { usePrimRateRules } from "@/hooks/usePrimRateRules";
import { DocsMenu } from "./DocsMenu";

export default function PersonnelMobileCards({
  rows,
  loading,
  view,
  isAdmin,
  canSeeKidem,
  canSeeMoney,
  hideCCRateBadge,
  selected,
  onToggleSelect,
  onEdit,
  onDelete,
  onAdvance,
  onKidemSim,
  onExitPdf,
  docsIndex,
  onDocsChanged,
}) {
  const { getRuleFor, isCallCenterPozisyon } = usePrimRateRules();
  return (
    <div className="md:hidden divide-y divide-slate-100" data-testid="personnel-cards">
      {loading && <Loading text="Personel listesi yükleniyor..." />}
      {!loading && rows.length === 0 && (
        <div className="py-10 text-center text-slate-400 text-sm">
          {view === "pasif" ? "Pasif personel yok" : "Henüz aktif personel kaydı yok"}
        </div>
      )}
      {rows.map((p) => (
        <div key={p.id} data-testid={`personnel-card-${p.sicil_no}`} className="p-4 flex items-start gap-3">
          {view === "aktif" && isAdmin && (
            <input
              data-testid={`select-m-${p.sicil_no}`}
              type="checkbox"
              checked={selected.has(p.id)}
              onChange={() => onToggleSelect(p.id)}
              className="mt-1"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="font-semibold text-slate-900 truncate">{p.ad_soyad}</div>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.aktif ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
                {p.aktif ? "Aktif" : "Pasif"}
              </span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5 truncate flex items-center gap-1.5">
              <span>{p.pozisyon || "—"}</span>
              {(() => {
                const rule = getRuleFor(p.pozisyon);
                if (!rule) return null;
                const isCC = isCallCenterPozisyon(p.pozisyon) || isCallCenterPozisyon(rule.pozisyon);
                if (isCC && hideCCRateBadge) return null;
                const label = isCC ? `CC %${(rule.rate * 100).toFixed(rule.rate * 100 % 1 === 0 ? 0 : 1)}` : `%${(rule.rate * 100).toFixed(rule.rate * 100 % 1 === 0 ? 0 : 1)}`;
                return (
                  <span
                    data-testid={`cc-badge-mobile-${p.sicil_no}`}
                    className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${isCC ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-indigo-100 text-indigo-800 border-indigo-300"}`}
                    title={`Prim Oran Kuralı · ${rule.pozisyon}`}
                  >
                    {label}
                  </span>
                );
              })()}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              <div><span className="text-slate-400">TC: </span><span className="font-mono-tab text-slate-700">{p.sicil_no}</span></div>
              <div><span className="text-slate-400">Başlama: </span><span className="font-mono-tab text-slate-700">{fmtDate(p.ise_baslama_tarihi)}</span></div>
              {p.ise_cikis_tarihi && (
                <div className="col-span-2">
                  <span className="text-slate-400">Çıkış: </span>
                  <span className="font-mono-tab text-rose-700 font-semibold">{fmtDate(p.ise_cikis_tarihi)}</span>
                </div>
              )}
              <div className="col-span-2">
                <span className="text-slate-400">Aylık Brüt: </span>
                <span className="font-mono-tab font-semibold text-slate-900">{canSeeMoney ? formatTL(p.aylik_brut) : "•••••"}</span>
              </div>
            </div>
          </div>
          {docsIndex && <DocsMenu p={p} docs={docsIndex[p.id] || []} onChanged={onDocsChanged} testPrefix="m-" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`personnel-actions-${p.sicil_no}`}
                className="shrink-0 w-9 h-9 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
              >
                <MoreVertical size={18} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {!p.aktif && canSeeKidem && (
                <DropdownMenuItem onSelect={() => onExitPdf(p)} data-testid={`m-exit-pdf-${p.sicil_no}`}>
                  <FileDown size={14} className="mr-2 text-rose-600" /> Çıkış Tazminatı PDF
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onAdvance(p)} data-testid={`m-advance-${p.sicil_no}`}>
                <Receipt size={14} className="mr-2 text-amber-600" /> Avans Ekle
              </DropdownMenuItem>
              {canSeeKidem && (
                <DropdownMenuItem onSelect={() => onKidemSim(p)} data-testid={`m-kidem-${p.sicil_no}`}>
                  <Award size={14} className="mr-2 text-emerald-600" /> Kıdem Simülasyonu
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={() => onEdit(p)} data-testid={`m-edit-${p.sicil_no}`}>
                <Edit size={14} className="mr-2 text-slate-600" /> Düzenle
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onDelete(p.id)} className="text-rose-600 focus:text-rose-700" data-testid={`m-delete-${p.sicil_no}`}>
                <Trash2 size={14} className="mr-2" /> Sil
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
    </div>
  );
}
